import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { adminApi } from '../../lib/api';
import { 
  Settings, Plus, Trash2, Crown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newFeature, setNewFeature] = useState({ name: '', description: '', tier_required: 'free' });

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await adminApi.getFeatures();
      setFeatures(response.data.features);
    } catch (error) {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const handleToggleFeature = async (featureId, currentEnabled) => {
    try {
      await adminApi.updateFeature(featureId, { enabled: !currentEnabled });
      toast.success('Feature updated');
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  const handleChangeTier = async (featureId, newTier) => {
    try {
      await adminApi.updateFeature(featureId, { tier_required: newTier });
      toast.success('Feature tier updated');
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  const handleCreateFeature = async () => {
    if (!newFeature.name || !newFeature.description) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      await adminApi.createFeature(newFeature);
      toast.success('Feature created');
      setCreateModalOpen(false);
      setNewFeature({ name: '', description: '', tier_required: 'free' });
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to create feature');
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (!window.confirm('Delete this feature flag?')) return;
    
    try {
      await adminApi.deleteFeature(featureId);
      toast.success('Feature deleted');
      fetchFeatures();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="admin-features-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
              Feature Flags
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Control app features and premium access</p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-[#4A9CC7] hover:bg-[#3A8CB7] rounded-full"
            data-testid="add-feature-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Feature
          </Button>
        </div>

        <div className="space-y-4">
          {features.map((feature) => (
            <Card key={feature.feature_id} data-testid={`feature-${feature.feature_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{feature.name}</h3>
                      <Badge variant={feature.enabled ? 'default' : 'secondary'} className={feature.enabled ? 'bg-green-500' : ''}>
                        {feature.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      {feature.tier_required === 'premium' && (
                        <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/30">
                          <Crown className="w-3 h-3 mr-1" /> Premium
                        </Badge>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{feature.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-4 ml-4">
                    <Select 
                      value={feature.tier_required} 
                      onValueChange={(v) => handleChangeTier(feature.feature_id, v)}
                    >
                      <SelectTrigger className="w-[120px] rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Switch
                      checked={feature.enabled}
                      onCheckedChange={() => handleToggleFeature(feature.feature_id, feature.enabled)}
                      data-testid={`toggle-${feature.feature_id}`}
                    />
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteFeature(feature.feature_id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {features.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No feature flags configured</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Create Feature Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent data-testid="create-feature-modal">
            <DialogHeader>
              <DialogTitle>Create Feature Flag</DialogTitle>
              <DialogDescription>Add a new feature flag to control app functionality</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Feature Name</label>
                <Input
                  value={newFeature.name}
                  onChange={(e) => setNewFeature({...newFeature, name: e.target.value})}
                  placeholder="e.g., dark_mode"
                  className="rounded-xl"
                  data-testid="feature-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={newFeature.description}
                  onChange={(e) => setNewFeature({...newFeature, description: e.target.value})}
                  placeholder="What does this feature do?"
                  className="rounded-xl"
                  data-testid="feature-description-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Required Tier</label>
                <Select value={newFeature.tier_required} onValueChange={(v) => setNewFeature({...newFeature, tier_required: v})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateFeature} className="bg-[#4A9CC7] hover:bg-[#3A8CB7]" data-testid="save-feature-btn">
                Create Feature
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminFeatures;
